/**
 * Environment-aware logger for the application
 * Respects NODE_ENV to control logging output
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'success' | 'task' | 'api';

/**
 * Format the optional `data` payload for log output. Errors include their
 * stack so pm2 captures it; plain objects are JSON-stringified with a small
 * limit so we don't blow up the log file.
 */
function formatLogData(data: unknown): string {
  if (data instanceof Error) {
    return `${data.message}\n${data.stack ?? ''}`.trim();
  }
  try {
    const json = JSON.stringify(data);
    if (json && json.length > 4000) {
      return `${json.slice(0, 4000)}…(truncated)`;
    }
    return json ?? String(data);
  } catch {
    return String(data);
  }
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isStaging = (process.env.NODE_ENV as string) === 'staging';
  private isProduction = process.env.NODE_ENV === 'production';

  private formatLogEntry(level: LogLevel, message: string, data?: any): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    // In production, only log warnings and errors
    if (this.isProduction) {
      return level === 'warn' || level === 'error';
    }

    // In staging, log info and above
    if (this.isStaging) {
      return level !== 'debug';
    }

    // In development, log everything
    return true;
  }

  private output(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) {
      return;
    }

    const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}]`;
    const message = `${prefix} ${entry.message}`;
    // Stringify data when present so pm2 captures the full payload, not [object Object].
    const dataPart = entry.data === undefined ? '' : ` ${formatLogData(entry.data)}`;
    const line = `${message}${dataPart}`;

    // DO NOT remove these console.* calls. This IS the project's console
    // sink — the whole logger module would be a no-op without them. Biome's
    // `lint/suspicious/noConsole` rule has stripped them twice before
    // (commits 2bba997a + 81e61174 restored; my Phase 5 lint sweep
    // 160c8462 stripped them again, breaking [Google Callback] /
    // [Auth] / every other logger.* call site silently for 24h). Each
    // console.* below carries its own ignore for this reason.
    switch (entry.level) {
      case 'debug':
        // biome-ignore lint/suspicious/noConsole: see comment above — required sink
        console.debug(line);
        break;
      case 'info':
      case 'success':
      case 'task':
      case 'api':
        // biome-ignore lint/suspicious/noConsole: see comment above — required sink
        console.log(line);
        break;
      case 'warn':
        // biome-ignore lint/suspicious/noConsole: see comment above — required sink
        console.warn(line);
        break;
      case 'error':
        // biome-ignore lint/suspicious/noConsole: see comment above — required sink
        console.error(line);
        break;
    }
  }

  debug(message: string, data?: any): void {
    const entry = this.formatLogEntry('debug', message, data);
    this.output(entry);
  }

  info(message: string, data?: any): void {
    const entry = this.formatLogEntry('info', message, data);
    this.output(entry);
  }

  warn(message: string, data?: any): void {
    const entry = this.formatLogEntry('warn', message, data);
    this.output(entry);
  }

  error(message: string, data?: any): void {
    const entry = this.formatLogEntry('error', message, data);
    this.output(entry);
  }

  success(message: string, data?: any): void {
    const entry = this.formatLogEntry('success', message, data);
    this.output(entry);
  }

  task(message: string, data?: any): void {
    const entry = this.formatLogEntry('task', message, data);
    this.output(entry);
  }

  api(message: string, data?: any): void {
    // Sanitize sensitive data before logging
    const sanitized = this.sanitizeData(data);
    const entry = this.formatLogEntry('api', message, sanitized);
    this.output(entry);
  }

  // Sanitize sensitive data from logs
  private sanitizeData(data: any): any {
    if (!data) {
      return data;
    }
    if (typeof data !== 'object') {
      return data;
    }

    const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'authorization'];
    const sanitized = { ...data };

    for (const key in sanitized) {
      if (sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive.toLowerCase()))) {
        sanitized[key] = '[REDACTED]';
      }
    }

    return sanitized;
  }

  // Log structured data for monitoring
  logMetric(metric: string, value: number, tags?: Record<string, string>): void {
    if (this.isDevelopment || this.isStaging) {
      this.info(`Metric: ${metric}`, { value, tags });
    }
  }

  // Log performance timing
  logTiming(operation: string, duration: number): void {
    if (this.isDevelopment || this.isStaging) {
      this.info(`Timing: ${operation}`, { duration: `${duration}ms` });
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };
